<template>
    <button @click="showModal = true" :disabled="disabled" :title="`Import ${importFileAccept} files`">
        <span>
            Import <font-awesome-icon icon="file-import" />
        </span>

        <teleport to="body">
            <modal v-if="showModal" @reject="showModal = false" @accept="importAction" accept-text="Import"
                :accept-disabled="importFiles.length === 0 || progress !== null" :title="`Import ${importFileAccept} files`">
                <div class="one-column-layout">
                    <div class="import-file-input">
                        <button :disabled="progress !== null" @click="chooseFiles">Choose files</button>
                        <span>{{ chosenFiles() }}</span>
                        <input type="file" :accept="importFileAccept" multiple @change="importFiles = $event.target.files"
                            autofocus tabindex="-1" ref="hiddenInput">
                    </div>
                    <span>{{ message }}</span>
                    <span v-if="progress !== null" class="red-text">Do not close or navigate away from this page until the import completes!</span>
                    <b-progress v-if="progress !== null" :animated="progress === 100" :value="progress"
                        :max="100"></b-progress>
                </div>
            </modal>
        </teleport>

        <file-upload :accept="importFileAccept" :multiple="true" :directory="false" :drop="!disabled" ref="upload"
            @input-filter="dropFilesFilter" @input="dropFiles">
        </file-upload>
        <div v-show="($refs.upload && $refs.upload.dropActive)" class="drop-active">
            <h3>
                {{ `Drop ${importFileAccept} files to import` }}<br><font-awesome-icon icon="file-import" />
            </h3>
        </div>
    </button>
</template>

<script>
import { notify } from '@/services/notify';
import FileUpload from 'vue-upload-component';
import Modal from '../Modal.vue';
import Teleport from 'vue2-teleport';
import imports from './imports';

export default {
    name: 'ImportAction',
    components: {
        FileUpload,
        Modal,
        Teleport,
    },
    props: {
        disabled: Boolean,
        entityName: String,
        entityNamePlural: String,
        importFileAccept: { type: String, default: '.json' },
        selectionCount: Number,
        importFunction: Function,
        importResource: String,
    },
    mounted() {
        // <file-upload> has a hidden input that grabs tab focus, turn it off.
        document.querySelector('input#file').setAttribute('tabindex', '-1');
    },
    data() {
        return {
            showModal: false,
            importFiles: [],
            progress: null,
            message: null,
        }
    },
    methods: {
        dropFilesFilter(newFile, oldFile, prevent) {
            if (newFile) {
                const extensions = this.importFileAccept.split(',').map(ext => ext.trim());
                if (extensions.find(ext => newFile.name.endsWith(ext)) === undefined) {
                    return prevent();
                }
            }
        },
        dropFiles(files) {
            this.importFiles = files.map(f => f.file);
            this.showModal = true;
        },
        chooseFiles() {
            this.$refs.hiddenInput.click();
        },
        chosenFiles() {
            if (this.importFiles.length === 0) return 'No files selected';
            if (this.importFiles.length === 1) return this.importFiles[0].name;
            return `${this.importFiles.length} files selected`
        },
        updateProgress(progressPercent, progressMessage) {
            this.progress = progressPercent;
            this.message = progressMessage;
        },
        async importAction() {
            try {
              if (this.importFunction) {
                await this.importFunction(this.importFiles, this.updateProgress);
                this.$emit('importAction');
              } else {
                await imports.json(this.importFiles, this.updateProgress, this.importResource);
                await this.$store.dispatch('task/fetchTasks');
                this.$store.commit('task/setTasksHaveUpdates', true);
              }
            }
            catch (err) {
                console.error(err);
                notify().error('Error importing a file.');
            }
            finally {
                this.showModal = false;
                this.importFiles = [];
                this.progress = null;
                this.message = null;
            }
        },
    },
}
</script>
<style scoped>
.import-file-input {
    display: flex;
    gap: 6px;
    align-items: end;
    margin-bottom: 10px;
}
.import-file-input>input {
    display: none;
}

.import-file-input button {
    border: 1px solid rgb(118, 118, 118, 0.75);
    border-radius: 2px;
    color: rgb(37, 36, 34);
    text-wrap: nowrap;
}
.import-file-input button:disabled {
    color: rgb(37, 36, 34, 0.25);
}
.import-file-input button:focus {
    outline: rgba(102, 179, 227, 0.75) auto 1px !important;
    outline-offset: 0px;
    z-index: 99;
}
.red-text {
    color: rgb(205, 15, 15);
}
</style>
